import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { OidcProvider } from "./auth/OidcProvider";
import { OauthCallback } from "./auth/OauthCallback";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { AdminRoute } from "./auth/AdminRoute";
import { ManagerRoute } from "./auth/ManagerRoute";
import { BrandedShell } from "./components/BrandedShell";
import { NotFoundPage } from "./pages/NotFoundPage";
import { LoginPage } from "./pages/auth/LoginPage";
import PromotionQueuePage from "./pages/promotion/PromotionQueuePage";
import { MyPromotions } from "./pages/promotion/MyPromotions";
import LibraryPage from "./pages/library/LibraryPage";
import SubjectsPage from "./pages/subjects/SubjectsPage";
import SchoolsAdminPage from "./pages/orgadmin/SchoolsAdminPage";
import SchoolDetailPage from "./pages/orgadmin/SchoolDetailPage";
import InvitationsAdminPage from "./pages/orgadmin/InvitationsAdminPage";
import AccessLogsPage from "./pages/orgadmin/AccessLogsPage";
import InviteAcceptPage from "./pages/invite/InviteAcceptPage";
import ConsultPage from "./pages/public/ConsultPage";
import KnowledgePointsPage from "./pages/kp/KnowledgePointsPage";
import CurriculumPage from "./pages/curriculum/CurriculumPage";
import BlueprintPage from "./pages/blueprint/BlueprintPage";
import PaperPage from "./pages/paper/PaperPage";
import ScoreGridPage from "./pages/scores/ScoreGridPage";
import TeacherStatePage from "./pages/teacher/TeacherStatePage";
import { NotesFilesPage } from "./pages/workbench/NotesFilesPage";
import { TeacherWorkbenchPage } from "./pages/workbench/TeacherWorkbenchPage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { ClassesPage } from "./pages/classes/ClassesPage";
import { ClassDetailPage } from "./pages/classes/ClassDetailPage";
import { ClassConductPage } from "./pages/gpa/ClassConductPage";
import { ClassGradesPage } from "./pages/gpa/ClassGradesPage";
import { SheetLandingPage } from "./pages/gpa/SheetLandingPage";
import { GpaConfigPage } from "./pages/gpa/GpaConfigPage";
import { GpaOverviewPage } from "./pages/gpa/GpaOverviewPage";
import { StudentDetailPage } from "./pages/students/StudentDetailPage";
import { AssignmentPage } from "./pages/assignments/AssignmentPage";
import { AdmissionPage } from "./pages/admissions/AdmissionPage";
import { InterviewPage } from "./pages/admissions/InterviewPage";
import { ExamPage } from "./pages/exams/ExamPage";
import { ITargetPage } from "./pages/itarget/ITargetPage";
import { ReportsPage } from "./pages/reports/ReportsPage";
import { TeachingPlanEditPage } from "./pages/teachingPlans/TeachingPlanEditPage";
import { TeachingPlansPage } from "./pages/teachingPlans/TeachingPlansPage";
import DingtalkPage from "./pages/dingtalk/DingtalkPage";
import ParentPage from "./pages/parent/ParentPage";
import { ClassLessonsPage } from "./pages/lessons/ClassLessonsPage";
import { ClassFeedbackPage } from "./pages/lessons/ClassFeedbackPage";
import { isGuardianOnly, useOrgMe } from "./api/school";

/** 首页:只是家长(钉钉家校通讯录,无老师档案)的用户直接去家长页。 */
function HomeRoute(): JSX.Element {
  const orgMe = useOrgMe();
  if (isGuardianOnly(orgMe.data)) return <Navigate to="/parent" replace />;
  return <TeacherWorkbenchPage />;
}

function ChatRedirect(): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={`/${search}`} replace />;
}

export function AppRoutes() {
  return (
    <OidcProvider>
      <Routes>
        {/* OIDC redirect target — must NOT be wrapped in ProtectedRoute. */}
        <Route path="/oauth/callback" element={<OauthCallback />} />
        {/* Email+password fallback for SES/locked-down browsers where the
            PKCE flow can't run. NOT wrapped in ProtectedRoute so the form
            actually renders for unauthenticated users. */}
        <Route path="/login" element={<LoginPage />} />
        {/* 公开邀请接受页(teacher-role spec §5.1)— 不进 ProtectedRoute。 */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        {/* 家长招生咨询(演示原型)— 公开页,不进 ProtectedRoute。 */}
        <Route path="/consult" element={<ConsultPage />} />
        {/* Everything else lives under the branded shell behind auth. */}
        <Route
          element={
            <ProtectedRoute>
              <BrandedShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeRoute />} />
          <Route path="parent" element={<ParentPage />} />
          <Route
            path="admin/dingtalk"
            element={
              <ManagerRoute>
                <DingtalkPage />
              </ManagerRoute>
            }
          />
          {/* /chat is a legacy redirect — Sidebar Recents now links at /,
              but bookmarks and external links to /chat?conversation=…
              still resolve. Query string is preserved. */}
          <Route path="chat" element={<ChatRedirect />} />
          <Route path="documents" element={<NotesFilesPage />} />
          <Route path="my-promotions" element={<MyPromotions />} />
          {/* 园长/集团管理员专属:后端本就 403,但没有路由守卫时教师会看到一张
              空白页(2026-09-03 交付验收发现),和 admin/invitations 一样包 ManagerRoute。 */}
          <Route
            path="library"
            element={
              <ManagerRoute>
                <LibraryPage />
              </ManagerRoute>
            }
          />
          <Route
            path="subjects"
            element={
              <ManagerRoute>
                <SubjectsPage />
              </ManagerRoute>
            }
          />
          <Route path="admin/promotions" element={<PromotionQueuePage />} />
          <Route path="kp" element={<KnowledgePointsPage />} />
          <Route path="curriculum" element={<CurriculumPage />} />
          <Route
            path="admin/schools"
            element={
              <ManagerRoute>
                <SchoolsAdminPage />
              </ManagerRoute>
            }
          />
          <Route
            path="admin/access-logs"
            element={
              <AdminRoute>
                <AccessLogsPage />
              </AdminRoute>
            }
          />
          <Route
            path="admin/invitations"
            element={
              <ManagerRoute>
                <InvitationsAdminPage />
              </ManagerRoute>
            }
          />
          <Route
            path="admin/schools/:schoolId"
            element={
              <ManagerRoute>
                <SchoolDetailPage />
              </ManagerRoute>
            }
          />
          <Route
            path="admin/dashboard"
            element={
              <AdminRoute>
                <DashboardPage />
              </AdminRoute>
            }
          />
          <Route path="teacher/state" element={<TeacherStatePage />} />
          {/* Nova School i5 MVP — Day 3-4 routes. Backend: services/school-backend. */}
          <Route path="classes" element={<ClassesPage />} />
          <Route path="classes/:classId" element={<ClassDetailPage />} />
          <Route path="classes/:classId/grades" element={<ClassGradesPage />} />
          <Route path="sheets/:sheetId" element={<SheetLandingPage />} />
          <Route path="classes/:classId/conduct" element={<ClassConductPage />} />
          <Route path="classes/:classId/lessons" element={<ClassLessonsPage />} />
          <Route path="gpa/overview" element={<GpaOverviewPage />} />
          <Route path="classes/:classId/feedback" element={<ClassFeedbackPage />} />
          <Route
            path="gpa/config"
            element={
              <ManagerRoute>
                <GpaConfigPage />
              </ManagerRoute>
            }
          />
          <Route path="students/:studentId" element={<StudentDetailPage />} />
          <Route path="assignments" element={<AssignmentPage />} />
          <Route path="exams" element={<ExamPage />} />
          <Route path="exams/:examId/blueprint" element={<BlueprintPage />} />
          <Route path="exams/:examId/paper" element={<PaperPage />} />
          <Route path="exams/:examId/scores" element={<ScoreGridPage />} />
          <Route path="admissions" element={<AdmissionPage />} />
          <Route path="admissions/interview/:admissionId" element={<InterviewPage />} />
          <Route path="itarget" element={<ITargetPage />} />
          <Route path="reports" element={<ReportsPage />} />
          {/* Legacy /extensions route — keeps old bookmarks alive. */}
          <Route path="extensions" element={<Navigate to="/teaching-plans" replace />} />
          <Route path="teaching-plans" element={<TeachingPlansPage />} />
          {/* Legacy /new redirect — the form lives on /teaching-plans now. */}
          <Route path="teaching-plans/new" element={<Navigate to="/teaching-plans" replace />} />
          <Route path="teaching-plans/:filename" element={<TeachingPlanEditPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </OidcProvider>
  );
}
